import { useLocation } from '@gatsbyjs/reach-router'
import * as R from 'ramda'
import { useCallback } from 'react'

import { isNotNull } from '../../../utils/collectionTools'
import { mergeUndefinedRight } from '../../../utils/ramda'
import { addFragmentToUrl, addQueryParamsToUrl, createQueryParam } from '../../../utils/url'
import { getLoginCallbackUrl } from '../../auth'
import { addDispensaryBasePathToPath, useDispensaryContext } from '../../dispensaries'
import { accountsReturnToParam } from '../config'
import { AccountsPageType, RegisterType } from '../types'
import { getAccountsUrlForPage } from '../utils'

export interface UseAccountsUrl {
  getRedirectUrl: (options: AccountsRedirectOptions) => string
  redirectToAccount: (options: AccountsRedirectOptions) => void
}

export type AccountsRedirectOptions =
  | {
      page?: Exclude<AccountsPageType, 'register'>
    }
  | AccountsRedirectOptionsForRegisterPage

type AccountsRedirectOptionsForRegisterPage = {
  page: Extract<AccountsPageType, 'register'>
  registerType?: RegisterType
}

const defaultOptions: AccountsRedirectOptions = {
  page: 'base',
}

const callbackUrlForReturnTo = R.pipe(
  (returnTo: string) => [createQueryParam(accountsReturnToParam, returnTo)].filter(isNotNull),
  addQueryParamsToUrl(getLoginCallbackUrl()),
)

const isOptionsForRegisterPage = (
  options: AccountsRedirectOptions,
): options is AccountsRedirectOptionsForRegisterPage => options?.page === 'register'

const addRegisterTypeFragment =
  (options: AccountsRedirectOptions) =>
  (url: string): string =>
    isOptionsForRegisterPage(options) && options.registerType
      ? addFragmentToUrl(url, options.registerType)
      : url

export const useAccountsUrl = (): UseAccountsUrl => {
  const location = useLocation()
  const dispensary = useDispensaryContext()

  const practitionerCode = dispensary ? dispensary.practitionerCode : null

  const getCurrentPath = useCallback(() => `${location.pathname}${location.search}`, [location])

  const getOnboardingPath = useCallback(
    () =>
      dispensary ? addDispensaryBasePathToPath(dispensary.slug, '/onboarding') : '/onboarding',
    [dispensary],
  )

  const getReturnPath = useCallback(
    (options: AccountsRedirectOptions): string =>
      isOptionsForRegisterPage(options) ? getOnboardingPath() : getCurrentPath(),
    [getCurrentPath, getOnboardingPath],
  )

  const getRedirectUrl = useCallback(
    (options: AccountsRedirectOptions = defaultOptions): string => {
      const redirectOptions = mergeUndefinedRight(defaultOptions, options)
      const accountsUrl = getAccountsUrlForPage(redirectOptions.page)
      const params = [
        createQueryParam('practitionerCode', practitionerCode),
        createQueryParam('callbackUrl', callbackUrlForReturnTo(getReturnPath(redirectOptions))),
      ]
      return R.pipe(
        R.filter<string>(isNotNull),
        addQueryParamsToUrl(accountsUrl),
        addRegisterTypeFragment(redirectOptions),
      )(params)
    },
    [practitionerCode, getReturnPath],
  )

  const redirectToAccount = useCallback(
    (options: AccountsRedirectOptions = defaultOptions): void => {
      if (typeof window === 'undefined') {
        return
      }
      window.location.href = getRedirectUrl(options)
    },
    [getRedirectUrl],
  )

  return {
    getRedirectUrl,
    redirectToAccount,
  }
}
